import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
const toast = { success: (m) => console.log("[toast]", m), error: (m) => console.error("[toast]", m), info: (m) => console.log("[toast]", m) };
import PageContainer, { PageHeader } from "@/components/PageContainer";
import api from "@/lib/assessments";
import { readAll } from "@/lib/assessments";
const formatApiError = (e) => (e && e.message) || "Unknown error";
import { Save, ChevronLeft } from "lucide-react";
import { COGNITIVE_ITEMS, interpretCognitive } from "@/data/instruments";

export default function CognitiveScreen() {
  const navigate = useNavigate();
  const [patient, setPatient] = useState("");
  const [notes, setNotes] = useState("");
  const [education, setEducation] = useState("hs");
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const total = COGNITIVE_ITEMS.reduce((a, it) => a + Math.min(it.max, Number(values[it.id] || 0)), 0);
  const complete = COGNITIVE_ITEMS.every((it) => values[it.id] !== undefined && values[it.id] !== "");
  const interp = complete ? interpretCognitive(total, education) : null;

  const save = async () => {
    if (!complete) {
      toast.error("Score every item before saving.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/assessments", {
        kind: "cognitive",
        patient_identifier: patient || null,
        responses: { values, education, total },
        score: total,
        interpretation: interp.band,
        notes: notes || null,
      });
      toast.success("Cognitive screen saved.");
      navigate("/portal/continuum/tools/history");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer testid="cognitive-page">
      <Link to="/portal/continuum/tools" className="text-sm text-ink-muted hover:text-navy inline-flex items-center gap-1 mb-4" data-testid="cognitive-back">
        <ChevronLeft size={14} strokeWidth={2} /> All tools
      </Link>

      <PageHeader overline="Portal · Tool" title="Cognitive Screen"
        subtitle="SLUMS-inspired 30-point cognitive screen. Interpretation bands are adjusted for education (high-school vs less-than-HS)." />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5 grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">Patient identifier</label>
              <input className="field" value={patient} onChange={(e) => setPatient(e.target.value)} data-testid="cognitive-patient" />
            </div>
            <div>
              <label className="label">Education</label>
              <select className="field" value={education} onChange={(e) => setEducation(e.target.value)} data-testid="cognitive-education">
                <option value="hs">High-school or higher</option>
                <option value="less">Less than high-school</option>
              </select>
            </div>
          </div>

          <div className="card p-5 space-y-4">
            {COGNITIVE_ITEMS.map((it, i) => (
              <div key={it.id} className="grid grid-cols-[1fr_auto] items-center gap-4 border-b border-line-soft pb-3 last:border-0 last:pb-0" data-testid={`cognitive-item-${it.id}`}>
                <div>
                  <div className="text-sm text-navy">{i + 1}. {it.label}</div>
                  <div className="text-xs text-ink-muted mt-0.5">Max {it.max} pts</div>
                </div>
                <input
                  type="number"
                  min={0}
                  max={it.max}
                  value={values[it.id] ?? ""}
                  onChange={(e) => setValues({ ...values, [it.id]: Math.min(it.max, Math.max(0, Number(e.target.value))) })}
                  className="field w-20 text-right"
                  data-testid={`cognitive-${it.id}`}
                />
              </div>
            ))}
          </div>

          <div className="card p-5">
            <label className="label">Notes</label>
            <textarea className="field min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="cognitive-notes" />
          </div>
        </div>

        <aside className="lg:sticky lg:top-6 self-start">
          <div className="card p-6" data-testid="cognitive-summary">
            <div className="overline mb-2">Score</div>
            <div className="text-5xl font-semibold text-navy">{total}<span className="text-xl text-ink-muted"> / 30</span></div>
            {interp ? (
              <div className="mt-4 fade-up">
                <div className="badge badge-teal mb-2">{interp.band}</div>
                <p className="text-sm text-ink leading-relaxed">{interp.detail}</p>
              </div>
            ) : (
              <div className="mt-4 text-sm text-ink-muted">Complete every item to see the band.</div>
            )}

            <div className="rule my-4" />
            <button onClick={save} disabled={!complete || saving} className="btn btn-primary w-full justify-center" data-testid="cognitive-save">
              {saving ? "Saving..." : (<><Save size={14} strokeWidth={2} /> Save to history</>)}
            </button>
          </div>
        </aside>
      </div>
    </PageContainer>
  );
}
